import apiClient from './client';
import type { 
  LoginRequest, 
  LoginResponse, 
  User, 
  RegisterRequest, 
  RegisterResponse,
  VerifyEmailRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  ResendVerificationRequest,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ValidateResetCodeRequest,
  ResetPasswordWithCodeRequest
} from '../types/auth.types';

/**
 * Authentication service for handling login, registration, and user authentication
 */
export const authService = {
  /**
   * Login with Email and password
   * @param identifier - User Email
   * @param password - User password
   * @returns LoginResponse with token and user data
   */
  login: async (identifier: string, password: string): Promise<LoginResponse> => {
    const loginRequest: LoginRequest = { identifier, password };
    const response = await apiClient.post<LoginResponse>('/api/Auth/login', loginRequest);
    return response.data;
  },

  /**
   * Register a new school with admin user
   * @param data - Registration data including school and admin information
   * @param logoFile - Optional school logo file
   * @returns RegisterResponse with school details and trial information
   */
  register: async (data: RegisterRequest, logoFile?: File): Promise<RegisterResponse> => {
    const formData = new FormData();
    
    // Add all fields with PascalCase names as per API requirements
    formData.append('SchoolName', data.schoolName);
    formData.append('Address', data.address);
    formData.append('ContactEmail', data.contactEmail);
    // Only sent when the address lookup found them - the school is registered either way
    if (data.city) formData.append('City', data.city);
    if (data.province) formData.append('Province', data.province);
    if (data.latitude != null) formData.append('Latitude', String(data.latitude));
    if (data.longitude != null) formData.append('Longitude', String(data.longitude));
    formData.append('ContactPhone', data.contactPhone);
    formData.append('AdminFirstName', data.adminFirstName);
    formData.append('AdminLastName', data.adminLastName);
    formData.append('Password', data.password);
    
    // Add logo file if provided
    if (logoFile) {
      formData.append('Logo', logoFile);
    }
    
    const response = await apiClient.post<RegisterResponse>('/api/Auth/register', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Verify email address using token from email
   * @param token - Verification token from email
   * @returns Success message
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const request: VerifyEmailRequest = { token };
    const response = await apiClient.post<{ message: string }>('/api/Auth/verify-email', request);
    return response.data;
  },

  /**
   * Request password reset email
   * @param email - User email address
   * @returns Success message
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const request: ForgotPasswordRequest = { email };
    const response = await apiClient.post<{ message: string }>('/api/Auth/forgot-password', request);
    return response.data;
  },

  /**
   * Reset password using token from email
   * @param token - Reset token from email
   * @param newPassword - New password
   * @returns Success message
   */
  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const request: ResetPasswordRequest = { token, newPassword };
    const response = await apiClient.post<{ message: string }>('/api/Auth/reset-password', request);
    return response.data;
  },

  /**
   * Change password for authenticated user
   * @param currentPassword - Current password
   * @param newPassword - New password
   * @returns Success message
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const request: ChangePasswordRequest = { currentPassword, newPassword };
    const response = await apiClient.post<{ message: string }>('/api/Auth/change-password', request);
    return response.data;
  },

  /**
   * Resend email verification link
   * @param email - User email address
   * @returns Success message
   */
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const request: ResendVerificationRequest = { email };
    const response = await apiClient.post<{ message: string }>('/api/Auth/resend-verification', request);
    return response.data;
  },

  /**
   * Request password reset (email or phone)
   * @param identifier - Email address or phone number
   * @returns Response indicating channel used (email or sms)
   */
  requestPasswordReset: async (identifier: string): Promise<RequestPasswordResetResponse> => {
    const request: RequestPasswordResetRequest = { identifier };
    const response = await apiClient.post<RequestPasswordResetResponse>('/api/Auth/request-password-reset', request);
    return response.data;
  },

  /**
   * Validate password reset code (for phone-based reset)
   * @param identifier - Phone number
   * @param code - 6-digit reset code
   * @returns Success message
   */
  validateResetCode: async (identifier: string, code: string): Promise<{ message: string }> => {
    const request: ValidateResetCodeRequest = { identifier, code };
    const response = await apiClient.post<{ message: string }>('/api/Auth/validate-reset-code', request);
    return response.data;
  },

  /**
   * Reset password with code (for phone-based reset)
   * @param identifier - Phone number
   * @param code - 6-digit reset code
   * @param newPassword - New password
   * @returns Success message
   */
  resetPasswordWithCode: async (identifier: string, code: string, newPassword: string): Promise<{ message: string }> => {
    const request: ResetPasswordWithCodeRequest = { identifier, code, newPassword };
    const response = await apiClient.post<{ message: string }>('/api/Auth/reset-password-with-code', request);
    return response.data;
  },

  /**
   * Get current authenticated user information
   * @returns User object with current user details
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/api/Auth/me');
    return response.data;
  },
};
